import React, { useEffect, useState } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Modal, Select, TextField, Typography } from "@mui/material";
import CustomIconButton from 'packages/novous_web/src/common/components/CustomIconButton';
import Button from 'packages/novous_web/src/common/components/Button';
import InputField from 'packages/novous_web/src/common/components/InputField';
import { countries } from './Countries';
import { UserAccountForm } from '../UserAccount';
import useAxios from 'packages/novous_web/src/api/useAxios';
import { API_ENDPOINTS } from 'packages/novous_web/src/api/endpoints';
import { useAuth } from 'packages/novous_web/src/authContext/useAuth';
import CustomDropDownInput from 'packages/novous_web/src/common/components/customDropDownInput';
import DOMPurify from 'dompurify';

interface CreateUserAccountProps {
    onSubmit: (formData: UserAccountForm) => void;
}

interface CreateUserAccountProps {
    isCreateAccountOpen: boolean;
    handleCreateAccountModalClose: () => void;
    onSubmit: (formData: UserAccountForm) => void;
    modalTitle: string;
    modalDescription: string;
    cancelButtonText: string;
    submitButtonText: string;
}

const CreateUserAccount: React.FC<CreateUserAccountProps> = ({
    isCreateAccountOpen,
    handleCreateAccountModalClose,
    onSubmit,

    modalTitle,
    modalDescription,
    cancelButtonText,
    submitButtonText
}) => {
    const { user } = useAuth();
    const [isFormValid, setIsFormValid] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [formData, setFormData] = useState<UserAccountForm>({
        firstName: '',
        userName: '',
        authorities: '',
        email: '',
        phone: '',
        address: {
            fullAddress: '',
            country: ''
        },
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState<UserAccountForm>({
        firstName: '',
        userName: '',
        authorities: '',
        email: '',
        phone: '',
        address: {
            fullAddress: '',
            country: ''
        },
        password: '',
        confirmPassword: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const sanitizedValue = DOMPurify.sanitize(value, {
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: [],
        });
        if (name.startsWith('address.')) {
            const addressField = name.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [addressField]: sanitizedValue
                }
            }));

            if (errors.address[addressField as keyof typeof errors.address]) {
                setErrors(prev => ({
                    ...prev,
                    address: {
                        ...prev.address,
                        [addressField]: ''
                    }
                }));
            }
        } else {
            // Handle regular fields
            setFormData(prev => ({
                ...prev,
                [name]: sanitizedValue
            }));
            if (errors[name as keyof typeof errors]) {
                setErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }
        }
    };

    const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }> | string, name?: string) => {
        let fieldName: string;
        let value: string;

        // If it's a string (direct value from CustomDropDownInput)
        if (typeof e === 'string') {
            fieldName = name as string;
            value = e;
        }
        // If it's an event object (from native Select)
        else {
            fieldName = e.target?.name as string;
            value = e.target?.value as string;
        }

        // Handle nested address fields
        if (fieldName.startsWith('address.')) {
            const addressField = fieldName.split('.')[1];
            setFormData(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    [addressField]: value
                }
            }));
        }
        // Handle regular fields
        else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: value
            }));
        }

        // Clear errors if they exist
        if (fieldName === 'authorities' && errors.authorities) {
            setErrors(prev => ({ ...prev, authorities: '' }));
        }
        if (fieldName === 'address.country' && errors.address.country) {
            setErrors(prev => ({
                ...prev,
                address: {
                    ...prev.address,
                    country: ''
                }
            }));
        }
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: UserAccountForm = {
            firstName: '',
            userName: '',
            authorities: '',
            email: '',
            phone: '',
            address: {
                fullAddress: '',
                country: ''
            },
            loginUserId: '',
            employeeId: '',
            password: '',
            confirmPassword: ''
        };

        if (!formData.firstName.trim()) {
            if (isSubmitted) newErrors.firstName = 'Name is required';
            valid = false;
        }

        if (!formData.userName.trim()) {
            if (isSubmitted) newErrors.userName = 'Username is required';
            valid = false;
        }

        if (!formData.authorities) {
            if (isSubmitted) newErrors.authorities = 'Role is required';
            valid = false;
        }

        if (!formData.email.trim()) {
            if (isSubmitted) newErrors.email = 'Email is required';
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            valid = false;
        }

        if (!formData.phone.trim()) {
            if (isSubmitted) newErrors.phone = 'Phone number is required';
            valid = false;
        }

        if (!formData.address.country) {
            if (isSubmitted) newErrors.address.country = 'Country is required';
            valid = false;
        }

        if (!formData.password) {
            if (isSubmitted) newErrors.password = 'Password is required';
            valid = false;
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
            valid = false;
        }

        if (!formData.confirmPassword) {
            if (isSubmitted) newErrors.confirmPassword = 'Confirm Password is required';
            valid = false;
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
            valid = false;
        }

        setErrors(newErrors);
        setIsFormValid(valid);
        return valid;
    };

    useEffect(() => {
        validateForm();
    }, [formData]);

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitted(true);
        if (validateForm()) {
            onSubmit(formData);
            setFormData({
                firstName: '',
                userName: '',
                authorities: '',
                email: '',
                phone: '',
                address: {
                    fullAddress: '',
                    country: ''
                },
                loginUserId: '',
                employeeId: '',
                password: '',
                confirmPassword: ''
            });
            setErrors({
                firstName: '',
                userName: '',
                authorities: '',
                email: '',
                phone: '',
                address: {
                    fullAddress: '',
                    country: ''
                },
                loginUserId: '',
                employeeId: '',
                password: '',
                confirmPassword: ''
            });
            setIsFormValid(false);
            setIsSubmitted(false);
            handleCreateAccountModalClose();
        }
    };

    const {
        data: roleUsersAccountSave,
        error: roleUsersAccountSaveError,
        loading: roleUsersAccountSaveLoading,
        fetchData: fetchroleUsersAccountSaveData,
    } = useAxios<any>(`${API_ENDPOINTS.users.USER_ACTIVATE_ROLE}`, "POST");

    useEffect(() => {
        fetchroleUsersAccountSaveData({
            userId: user.id
        });
    }, [])

    return (
        <Modal
            open={isCreateAccountOpen}
            onClose={handleCreateAccountModalClose}
            aria-labelledby="download-modal-title"
            aria-describedby="download-modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100%',
                    maxWidth: '880px',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 4,
                }}
            >
                <div className="flex flex-col">
                    <Typography variant="h6" sx={{ color: (theme) => theme.palette.text.primary }}>
                        {modalTitle}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: (theme) => theme.palette.text.secondary,
                            fontSize: '14px',
                            lineHeight: '20px',
                            marginBottom: 4,
                        }}
                    >
                        {modalDescription}
                    </Typography>

                    <FormControl fullWidth>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <InputField
                                    label="Name"
                                    name="firstName"
                                    placeholder="Enter First Name"
                                    value={formData.firstName}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.firstName ? "error" : formData.firstName ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.firstName : ''}
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="Username"
                                    name="userName"
                                    placeholder="Enter User Name"
                                    value={formData.userName}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.userName ? "error" : formData.userName ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.userName : ''}
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <CustomDropDownInput
                                    label="Role"
                                    placeholder="Select Role"
                                    value={formData.authorities}
                                    onChange={(value) => handleSelectChange(value, 'authorities')}
                                    options={
                                        roleUsersAccountSave?.data.map((role: string) => {
                                            const formatRoleName = (role: string) => {
                                                return role
                                                    .replace('ROLE_', '')
                                                    .toLowerCase()
                                                    .split('_')
                                                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                                    .join(' ');
                                            };

                                            return {
                                                value: role,
                                                label: formatRoleName(role)
                                            };
                                        }) || []
                                    }
                                    isMandatory
                                />
                            </div>
                            {/* <div className="flex-1">
                                <Typography variant="h6" fontSize="14px" fontWeight={600} lineHeight='normal' letterSpacing='0%'>
                                    Role
                                </Typography>
                                <FormControl fullWidth sx={{ bgcolor: 'background.paper', borderRadius: '8px', marginTop: '0.8rem' }}>
                                    <Select
                                        name="authorities"
                                        value={formData.authorities}
                                        onChange={handleSelectChange}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Without label' }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontSize: '14px',
                                                border: '1px solid #F2F2F2',
                                                padding: '0.6rem',
                                            }
                                        }}
                                    >
                                        <MenuItem value="" sx={(theme) => ({
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: theme.palette.text.primary
                                        })}>
                                            Select Role
                                        </MenuItem>
                                        {roleUsersAccountSave && roleUsersAccountSave.data.map((role: any) => {
                                            const formatRoleName = (role: string) => {
                                                return role
                                                    .replace('ROLE_', '')
                                                    .toLowerCase()
                                                    .split('_')
                                                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                                                    .join(' ');
                                            };

                                            return (
                                                <MenuItem
                                                    key={role}
                                                    value={role}
                                                    sx={(theme) => ({
                                                        fontSize: '14px',
                                                        fontWeight: 400,
                                                        color: theme.palette.text.primary,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.secondary.light,
                                                            borderRadius: '0'
                                                        }
                                                    })}
                                                >
                                                    {formatRoleName(role)}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                                {isSubmitted && errors.authorities && (
                                    <Typography variant="caption" color="error" className="mt-1 flex items-center">
                                        {isSubmitted ? errors.authorities : ''}
                                    </Typography>
                                )}
                            </div> */}
                            <div className="flex-1">
                                <InputField
                                    label="Email Address"
                                    name="email"
                                    placeholder="Enter Email Address"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.email ? "error" : formData.email ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.email : ''}
                                />
                            </div>
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <InputField
                                    label="Contact Number"
                                    type='number'
                                    placeholder="Enter Contact Number"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.phone ? "error" : formData.phone ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.phone : ''}
                                />
                            </div>
                            <div className="flex-1">
                                <CustomDropDownInput
                                    label="Country"
                                    placeholder="Select Country"
                                    value={formData.address.country}
                                    onChange={(value) => handleSelectChange(value, 'address.country')}
                                    options={countries.map(country => ({
                                        value: country,
                                        label: country
                                    }))}
                                    isMandatory
                                />
                            </div>
                            {/* <div className="flex-1">
                                <Typography variant="h6" fontSize="14px" fontWeight={600} lineHeight='normal' letterSpacing='0%'>
                                    Country
                                </Typography>
                                <FormControl fullWidth sx={{ bgcolor: 'background.paper', borderRadius: '8px', marginTop: '0.8rem' }}>
                                    <Select
                                        name="address.country"
                                        value={formData.address.country}
                                        onChange={handleSelectChange}
                                        displayEmpty
                                        inputProps={{ 'aria-label': 'Without label' }}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                fontSize: '14px',
                                                border: '1px solid #F2F2F2',
                                                padding: '0.6rem',
                                            }
                                        }}
                                    >
                                        <MenuItem value="" sx={(theme) => ({
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: theme.palette.text.primary
                                        })}>
                                            Select Country
                                        </MenuItem>
                                        {countries.map((country) => (
                                            <MenuItem
                                                key={country}
                                                value={country}
                                                sx={(theme) => ({
                                                    fontSize: '14px',
                                                    fontWeight: 400,
                                                    color: theme.palette.text.primary,
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.secondary.light,
                                                        borderRadius: '0'
                                                    }
                                                })}
                                            >
                                                {country}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {isSubmitted && errors.address.country && (
                                    <Typography variant="caption" color="error" className="mt-1 flex items-center">
                                        {isSubmitted ? errors.address.country : ''}
                                    </Typography>
                                )}
                            </div> */}
                        </div>

                        <div className="mb-4">
                            <Typography variant="body2" fontWeight={600} color="textPrimary" className="self-start flex items-center space-x-1">
                                <span>Address</span>
                            </Typography>

                            <textarea
                                name="address.fullAddress"
                                value={formData.address.fullAddress}
                                onChange={handleChange}
                                placeholder="Enter your message"
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    borderRadius: '8px',
                                    borderColor: 'background.paper',
                                    fontSize: '14px',
                                    outline: 'none',
                                    resize: 'vertical',
                                    marginTop: '8px',
                                }}
                                className='mt-2 p-3 text-black border border-gray-300'
                            />
                        </div>

                        <div className="flex gap-4 mb-4">
                            <div className="flex-1">
                                <InputField
                                    label="Create Password"
                                    name="password"
                                    placeholder="Enter Your Password"
                                    type="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.password ? "error" : formData.password ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.password : ''}
                                />
                            </div>
                            <div className="flex-1">
                                <InputField
                                    label="Confirm Password"
                                    name="confirmPassword"
                                    placeholder="Confirm Your Password"
                                    type="password"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    isMandatory
                                    status={isSubmitted && errors.confirmPassword ? "error" : formData.confirmPassword ? "success" : undefined}
                                    errorMessage={isSubmitted ? errors.confirmPassword : ''}
                                />
                            </div>
                        </div>
                    </FormControl>

                </div>
                <div className="flex gap-2 justify-between mt-8">

                    <CustomIconButton
                        text={cancelButtonText}
                        background="transparent" outline={true} padding="0px"
                        variant="outlined"
                        onClick={handleCreateAccountModalClose}
                    />

                    <Button
                        text={submitButtonText}
                        onClick={handleFormSubmit}
                        fullWidth={false}
                    />
                </div>
            </Box>
        </Modal>
    );
}

export default CreateUserAccount;